/**
 * 학습 계획 정적 인덱스(scripts/build-study-plan-index.mjs 생성) 타입 + 순수 헬퍼. (JCG-2310)
 *
 * 빌드타임에 이론 4주 / 코딩 6주 MDX를 파싱한 `study-plan-index.json` 을 읽어,
 * "오늘의 학습" 위젯 · 온트랙 카드 · /my/schedule 캘린더가 런타임 MDX 컴파일 없이 소비한다.
 * 각 task.path 는 학습 페이지 경로이며 출석(attendance_records.slug)과 매칭해 완료를 도출한다.
 */
import studyPlanIndexData from "./study-plan-index.json";

export type StudyPlanId = "theory-4week" | "coding-6week";

export type StudyTaskKind =
  | "theory"
  | "coding"
  | "problem"
  | "gamja"
  | "king-gamja"
  | "random-quiz"
  | "mock-exam";

export interface StudyPlanTask {
  /** 이모지를 제거한 페이지 제목 */
  title: string;
  /** 내부 경로(앵커 포함 가능). 출석 slug 매칭 키 */
  path: string;
  /** 출제확률/시험 표시 이모지 ("" 가능) */
  emoji: string;
  /** 출제확률(99.9/80/50/20) 또는 null */
  probability: number | null;
  kind: StudyTaskKind;
  /** `(관련 페이지 - ...)` 로 부모 task 에 종속된 보조 링크 */
  isRelated: boolean;
  /**
   * 다국어 코딩 개념 페이지(coding/common/*)의 언어 차원. 'c'|'java'|'python'. (JCG-2658)
   * 빌드 시 언어별로 확장된 task 에만 존재하고, 그 외(이론·단일언어·관련링크)는 undefined(=언어 무관).
   * 출석 매칭 키·override taskKey 에 반영되어 언어별로 독립 완료/이동된다.
   */
  codeLang?: string;
}

export interface StudyPlanDay {
  /** 1-based */
  dayNumber: number;
  month: number;
  date: number;
  weekday: string;
  /** 회차 기준 정규 날짜(YYYY-MM-DD). 개인 시작일이 다르면 재앵커링해 사용 */
  dateISO: string;
  /** 본문 요일 뒤 🥔(화이팅/주말) 표시 */
  isWeekendMark: boolean;
  isExamDay: boolean;
  phaseIndex: number;
  phaseLabel: string;
  /** 일자 제목(코딩만 존재, 이론은 "") */
  title: string;
  /** 링크가 아닌 안내 텍스트("" 가능) */
  note: string;
  tasks: StudyPlanTask[];
}

export interface StudyPlanPhase {
  index: number;
  label: string;
  startDay: number;
  endDay: number;
}

export interface StudyPlan {
  planId: StudyPlanId;
  category: "theory" | "coding";
  title: string;
  /** 계획 본문 페이지 경로 */
  pagePath: string;
  /** "2026년 2회", "2026년 3회" */
  examSessionLabel: string;
  examYear: number;
  /** 회차 기준 Day 1 정규 날짜 */
  startDateISO: string;
  /** 회차 기준 마지막 Day(시험일) 정규 날짜 */
  examDateISO: string;
  totalDays: number;
  phases: StudyPlanPhase[];
  days: StudyPlanDay[];
  /** 이 계획 날짜 앵커가 속한 시험 회차(예: "2026-2") */
  examSessionId: string;
}

export type StudyPlanIndex = Record<
  string,
  Partial<Record<StudyPlanId, StudyPlan>>
>;

const studyPlanIndex = studyPlanIndexData as unknown as StudyPlanIndex;

export const STUDY_PLAN_IDS: StudyPlanId[] = ["theory-4week", "coding-6week"];

export const STUDY_PLAN_LABELS: Record<StudyPlanId, string> = {
  "theory-4week": "이론 4주 완성",
  "coding-6week": "코딩 6주 완성",
};

export function isStudyPlanId(value: string): value is StudyPlanId {
  return (STUDY_PLAN_IDS as string[]).includes(value);
}

export function getStudyPlan(
  planId: StudyPlanId,
  examSessionId: string,
): StudyPlan | null {
  return studyPlanIndex[examSessionId]?.[planId] ?? null;
}

export function getAllStudyPlans(examSessionId: string): StudyPlan[] {
  return STUDY_PLAN_IDS.map((id) => studyPlanIndex[examSessionId]?.[id]).filter(
    (p): p is StudyPlan => Boolean(p),
  );
}

/**
 * 회차 id("2026-2") 시간순 비교자. 연도 → 회차 번호 순으로 본다.
 * 회차 번호는 10회를 넘지 않지만 문자열 비교("2026-10" < "2026-2")에 기대지 않는다.
 */
function compareExamSessionIds(a: string, b: string): number {
  const [aYear = 0, aRound = 0] = a.split("-").map(Number);
  const [bYear = 0, bRound = 0] = b.split("-").map(Number);
  return aYear !== bYear ? aYear - bYear : aRound - bRound;
}

/**
 * 정적 계획이 준비된 회차 목록을 시험 시간순(오래된→최신)으로 반환한다.
 *
 * JSON 키 선언 순서에 기대지 않는다 — getSelectableStudyPlanSessionIds 가 위치로 자르므로,
 * study-plan-sessions.json 을 최신순으로 정렬하거나 새 회차를 앞에 끼워 넣으면 미공개 회차가
 * 선택 가능해진다. 정렬을 읽는 쪽에서 보장해 선언 순서와 무관하게 만든다.
 */
export function getStudyPlanSessionIds(): string[] {
  return Object.keys(studyPlanIndex).sort(compareExamSessionIds);
}

/**
 * 활성 회차와 그 이전에 공개된 학습 계획 회차만 반환한다.
 * 2026-2 활성 중 미리 생성해 둔 2026-3 계획이 클라이언트에서 선노출되는 것을 막는다.
 */
export function getSelectableStudyPlanSessionIds(
  activeExamSessionId: string,
): string[] {
  const ids = getStudyPlanSessionIds();
  const activeIndex = ids.indexOf(activeExamSessionId);
  return activeIndex >= 0 ? ids.slice(0, activeIndex + 1) : [];
}

export function isStudyPlanSessionSelectable(
  targetExamSessionId: string,
  activeExamSessionId: string,
): boolean {
  return getSelectableStudyPlanSessionIds(activeExamSessionId).includes(
    targetExamSessionId,
  );
}

export function getStudyPlanSessionLabel(examSessionId: string): string {
  const plan = STUDY_PLAN_IDS.map(
    (id) => studyPlanIndex[examSessionId]?.[id],
  ).find(Boolean);
  return plan?.examSessionLabel ?? examSessionId;
}

// ── 순수 날짜 헬퍼 (date-only, UTC 자정 기준으로 TZ 드리프트 방지) ──

/** ISO 날짜(YYYY-MM-DD)에 n일 더한 ISO 날짜 */
export function addDaysISO(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' → 'M/D' */
export function formatMonthDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

/** bISO - aISO (일수). 음수 가능 */
export function diffDaysISO(aISO: string, bISO: string): number {
  const a = new Date(`${aISO}T00:00:00Z`).getTime();
  const b = new Date(`${bISO}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000);
}

/** 개인 시작일(startISO) 기준 dayNumber(1-based)의 실제 날짜 */
export function anchoredDateISO(startISO: string, dayNumber: number): string {
  return addDaysISO(startISO, dayNumber - 1);
}

/** 개인 시작일 기준, 특정 날짜가 계획의 몇 일차인지(1-based). 범위 밖이면 null */
export function dayNumberForDate(
  plan: StudyPlan,
  startISO: string,
  dateISO: string,
): number | null {
  const n = diffDaysISO(startISO, dateISO) + 1;
  return n >= 1 && n <= plan.totalDays ? n : null;
}

/** 완료 판정에 쓸 task path 목록(앵커 제거된 순수 경로). 중복 제거 */
export function planTaskPaths(plan: StudyPlan): string[] {
  const set = new Set<string>();
  for (const day of plan.days) {
    for (const task of day.tasks) {
      if (task.path) set.add(task.path.split("#")[0]);
    }
  }
  return [...set];
}
